import { useAuthStore } from './session.ts';
import { Endpoints } from './contracts/endpoints.ts';
import { registerHandler as route } from './adapter.ts';
import { db, find, insert, update, values } from './helpers.ts';
import { ADMIN_ID } from './seed.ts';
import type { MockRecord } from './database.ts';
import { administrationScope, assignments, assertAdministrator, assertRecordScope, assignmentPatch, profilePslPatch, replacementAssignments, resolvePsl } from './adminPolicy.ts';
import { assertNoPendingRequest, assertUnregistered, directoryIdentityEligible, editableProfile, submitInternalRequest } from './adminDirectory.ts';
import { accessProfileFor } from './accessProfile.ts';
import { getActiveRequest, type MockRequest } from './adapter.ts';
import type { AccessPermissionKey } from './contracts/accessModel.ts';
import { evaluateAccess, permissionScope } from './contracts/accessEvaluator.ts';

function assertDirectoryIdentity(user: MockRecord): void {
  if (user.portal !== 'QCDOC' || !user.directoryIdentityId) return;
  const identity = db().directory?.find((entry) => entry.oid === user.directoryIdentityId);
  if (!identity || !directoryIdentityEligible(identity)) throw new Error('The verified synthetic directory identity is unavailable, disabled or ineligible.');
}

export function currentUser(): MockRecord {
  const session = useAuthStore.getState();
  if (!session.accessToken || !session.expiresAt || !Number.isFinite(Date.parse(session.expiresAt)) || Date.parse(session.expiresAt) <= Date.now() ||
    (session.accessToken !== 'qcdoc-prototype-access-token' && !session.accessToken.startsWith('mock-session:'))) throw new Error('Your session has expired. Sign in again.');
  const id = session.accessToken === 'qcdoc-prototype-access-token' ? ADMIN_ID : session.accessToken.replace('mock-session:', '');
  const user = find('users', id);
  if (!user.isApproved || user.isLockedOut) throw new Error('This test account is inactive or locked.');
  const selected = session.user && session.user.sub === String(user.userId ?? user.oid) ? session.user : null;
  const merged = selected ? { ...user, ...selected, roles: selected.groups ?? user.roles, userId: String(user.userId ?? user.oid) } : user;
  assertDirectoryIdentity(merged);
  return merged;
}

export function requireRoles(roles: string[]): MockRecord {
  const user = currentUser();
  const request = getActiveRequest();
  if (request && authorizeRequestPermission(user, request)) return user;
  const scope = administrationScope(user);
  // Empty requirements denote administration/reference data, not "any authenticated user".
  if (!roles.length) { assertAdministrator(user); return user; }
  const requested = new Set(roles.map(normalizeLegacyRole));
  const matchingAssignment = assignments(user).some((role) => requested.has(normalizeLegacyRole(role.roleName)));
  if (!scope.global && !matchingAssignment) throw new Error('This account does not have permission for this action.');
  return user;
}

export function authorizeRequestPermission(user: MockRecord, request: MockRequest): boolean {
  if (user.portal !== 'QCDOC') return false;
  const requestedPermission = requestPermission(request);
  if (!requestedPermission) return false;
  const profile = accessProfileFor(user);
  const rawRecordPsl = requestPsl(request);
  if (rawRecordPsl?.includes(',') && requestedPermission.startsWith('Products.')) {
    const pslIds = rawRecordPsl.split(',').map((value) => resolvePsl(value.trim())?.oid ?? value.trim()).filter(Boolean);
    const allowed = requestedPermission === 'Products.View'
      ? pslIds.some((pslId) => evaluateAccess(profile, requestedPermission, pslId).allowed)
      : pslIds.length > 0 && pslIds.every((pslId) => evaluateAccess(profile, requestedPermission, pslId).allowed);
    if (!allowed) throw new Error(`You do not have ${requestedPermission} permission for the selected PSLs.`);
    return true;
  }
  const recordPsl = resolvePsl(rawRecordPsl)?.oid ?? rawRecordPsl;
  const sampleLookup = request.method === 'GET' && [Endpoints.samples.productLookup, Endpoints.samples.categoryLookup].some((path) => path.toLowerCase() === request.path.toLowerCase());
  const permission = sampleLookup ? (['Samples.Create', 'Samples.Edit', 'Samples.View'] as AccessPermissionKey[]).find((key) =>
    recordPsl ? evaluateAccess(profile, key, recordPsl).allowed : profile.psls.some((psl) => psl.isActive && !psl.isGlobal && evaluateAccess(profile, key, psl.pslId).allowed)) ?? requestedPermission : requestedPermission;
  const catalogEntry = profile.permissions.find((entry) => entry.permissionKey === permission);
  const allowed = catalogEntry?.scopeRule === 'global-only' ? permissionScope(profile, permission).global
    : catalogEntry?.scopeRule === 'record' && (permission.endsWith('.View') || sampleLookup) && !recordPsl
    ? profile.psls.some((psl) => psl.isActive && !psl.isGlobal && evaluateAccess(profile, permission, psl.pslId).allowed)
    : !(catalogEntry?.scopeRule === 'record' && !recordPsl) && evaluateAccess(profile, permission, recordPsl).allowed;
  if (allowed) return true;
  const pslName = profile.psls.find((psl) => psl.pslId === recordPsl)?.name ?? rawRecordPsl;
  throw new Error(`You do not have ${permission} permission${pslName ? ` in ${String(pslName)}` : ''}.`);
}

export function filterRequestResponse(user: MockRecord, request: MockRequest, response: unknown): unknown {
  if (user.portal !== 'QCDOC') return response;
  const permission = requestPermission(request);
  if (!permission || !permission.endsWith('.View')) return response;
  const profile = accessProfileFor(user);
  if (Array.isArray(response)) {
    return response.filter((value) => isVisibleRecord(profile, permission, value));
  }
  if (response && typeof response === 'object') {
    const record = response as Record<string, unknown>;
    if ('oid' in record || 'id' in record || 'pslId' in record || 'psl' in record) {
      if (!isVisibleRecord(profile, permission, record)) throw new Error(`You do not have ${permission} permission for this record.`);
    }
  }
  return response;
}

function isVisibleRecord(profile: ReturnType<typeof accessProfileFor>, permission: AccessPermissionKey, value: unknown): boolean {
  if (!value || typeof value !== 'object') return false;
  const record = value as MockRecord;
  if (permission === 'Products.View') {
    const pslIds = String(record.pslID ?? record.pslId ?? '').split(',').map((pslId) => resolvePsl(pslId.trim())?.oid).filter(Boolean);
    if (pslIds.length) return pslIds.some((pslId) => evaluateAccess(profile, permission, pslId!).allowed);
  }
  const pslId = recordPslId(record);
  const catalogEntry = profile.permissions.find((entry) => entry.permissionKey === permission);
  if (catalogEntry?.scopeRule === 'record' && !pslId) return false;
  return evaluateAccess(profile, permission, pslId).allowed;
}

function recordPslId(record: MockRecord, depth = 0): string | null {
  if (depth > 4) return null;
  const direct = record.pslId ?? record.pslID ?? record.pslid ?? record.psl ?? record.pslName;
  if (typeof direct === 'string' && direct.includes(',')) return direct;
  const resolved = resolvePsl(direct);
  if (resolved) return resolved.oid;
  const relationships: [string, string][] = [
    ['vendorId', 'users'],
    ['productId', 'products'], ['prodId', 'products'], ['vqcaId', 'vqcas'], ['agreementId', 'vqcas'],
    ['sampleId', 'samples'], ['sampleOid', 'samples'], ['coaId', 'coas'], ['sourceId', 'coas'],
  ];
  for (const [field, table] of relationships) {
    const id = record[field];
    if (!id) continue;
    const parent = db()[table]?.find((entry) => entry.oid === id);
    if (parent) return recordPslId(parent, depth + 1);
  }
  return null;
}

export function filterPagedRequestResponse(user: MockRecord, request: MockRequest, response: unknown): unknown {
  if (!response || typeof response !== 'object' || Array.isArray(response)) return filterRequestResponse(user, request, response);
  const record = response as Record<string, unknown>;
  if (!Array.isArray(record.items)) return filterRequestResponse(user, request, response);
  const items = filterRequestResponse(user, request, record.items) as unknown[];
  // Handlers scope before paging, so an unchanged page keeps its true total.
  return items.length === record.items.length ? response : { ...record, items, totalCount: items.length };
}

function normalizeLegacyRole(value: string): string {
  const base = value.trim().replace(/\s+-\s+.+$/, '').toLowerCase();
  return ({
    admin: 'administrator', superadmin: 'administrator', qcdocadmin: 'administrator',
    cm: 'category manager', gql: 'global quality lead', qalab: 'qa lab', qalabsupervisor: 'qa lab supervisor',
    qatech: 'quality documenter', readonly: 'qa view', coaadmin: 'coa administrator', reportviewer: 'report viewer',
    productowner: 'psd', packagingadmin: 'packaging administrator',
  } as Record<string, string>)[base] ?? base;
}

function requestPermission(request: MockRequest): AccessPermissionKey | undefined {
  const path = request.path.toLowerCase();
  const method = request.method.toUpperCase();
  if (path === Endpoints.stp.create.toLowerCase()) return method === 'POST' ? 'STP.Create' : method === 'DELETE' ? 'STP.Delete' : 'STP.Edit';
  if (path.startsWith('/api/stp/')) {
    if (path.includes('approve') || path.includes('supersede') || path.includes('deactivate') || path.includes('reactivate')) return 'STP.Approve';
    if (path === Endpoints.stp.delete.toLowerCase() && method === 'DELETE') return 'STP.Delete';
    if (path === Endpoints.stp.list.toLowerCase() || method === 'GET') return 'STP.View';
    if (method === 'POST') return 'STP.Create';
    return 'STP.Edit';
  }
  if (path === Endpoints.coa.create.toLowerCase() || path === Endpoints.coa.submit.toLowerCase()) return 'COA.Create';
  if (path === Endpoints.coa.delete.toLowerCase()) return method === 'DELETE' ? 'COA.Delete' : 'COA.View';
  if (path.startsWith('/api/coa/')) {
    if ([Endpoints.coa.list, Endpoints.coa.listExport, Endpoints.coa.count, Endpoints.coa.getByVendor, Endpoints.coa.getByVendorList].some((endpoint) => path === endpoint.toLowerCase())) return 'COA.View';
    if (path.includes('approve') || path.includes('setfailedstatus')) return 'COA.Review';
    if (path.includes('undelete')) return 'COA.Restore';
    if (path.includes('delete')) return 'COA.Delete';
    if (path.includes('newcoa') || path === Endpoints.coa.create.toLowerCase() || path === Endpoints.coa.submit.toLowerCase()) return 'COA.Create';
    if (method === 'GET') return 'COA.View';
    return 'COA.Edit';
  }
  if (path === '/api/product') return method === 'DELETE' ? 'Products.Delete' : 'Products.View';
  if (path.startsWith('/api/product/') || path.startsWith('/api/prodspec/')) {
    if (path === Endpoints.products.list.toLowerCase() || path === Endpoints.products.count.toLowerCase()) return 'Products.View';
    if (method === 'GET' && [Endpoints.products.getApprovedSuppliers, Endpoints.products.getApprovedVQCASuppliers].some((endpoint) => path === endpoint.toLowerCase())) return 'Products.View';
    if (path.includes('approve')) return 'Products.Approve';
    if (method === 'GET' || path.includes('list') || path.includes('count') || path.includes('bypsl')) return 'Products.View';
    return method === 'POST' ? 'Products.Create' : 'Products.Edit';
  }
  if (path === Endpoints.vqca.create.toLowerCase() && method === 'POST') return 'VQCA.Create';
  if (path.startsWith('/api/vqca/') || path === Endpoints.vqca.create.toLowerCase()) {
    if (path === Endpoints.vqca.list.toLowerCase() || path === Endpoints.vqca.listPaged.toLowerCase() || method === 'GET') return 'VQCA.View';
    if (path.includes('signgql') || path.includes('rejectgql')) return 'VQCA.SignGql';
    if (path.includes('signcm') || path.includes('rejectcm')) return 'VQCA.SignCm';
    if (path.includes('deactivate')) return 'VQCA.Edit';
    if (path.includes('create') || path.includes('from-request') || path === Endpoints.vqca.create.toLowerCase()) return 'VQCA.Create';
    return path.includes('search') || path.includes('vendor') ? 'VQCA.View' : 'VQCA.Edit';
  }
  if (path === Endpoints.vendorRequests.create.toLowerCase() && method === 'POST') return 'VQCARequests.Create';
  if (path === Endpoints.vendorRequests.update.toLowerCase() && method === 'PUT') return 'VQCARequests.Edit';
  if (path.startsWith('/api/vqcareq/') || path === Endpoints.vendorRequests.list.toLowerCase()) {
    if (path.includes('/status/')) return 'VQCARequests.Approve';
    if (path === Endpoints.vendorRequests.create.toLowerCase() && method === 'POST') return 'VQCARequests.Create';
    if (path === Endpoints.vendorRequests.searchPaged.toLowerCase() || method === 'GET') return 'VQCARequests.View';
    if (method === 'DELETE') return 'VQCARequests.Delete';
    return 'VQCARequests.Edit';
  }
  if (path.startsWith('/api/sample/')) {
    if (method === 'GET' && [Endpoints.samples.productLookup, Endpoints.samples.categoryLookup].some((endpoint) => path === endpoint.toLowerCase())) return 'Samples.Create';
    if (path.includes('pass') || path.includes('fail') || path.includes('reopen') || path.includes('inprocess')) return 'Samples.Approve';
    if (path === Endpoints.samples.create.toLowerCase()) return 'Samples.Create';
    if ([Endpoints.samples.list, Endpoints.samples.count, Endpoints.samples.getAll, Endpoints.samples.siteSearch].some((endpoint) => path === endpoint.toLowerCase())) return 'Samples.View';
    return method === 'GET' ? 'Samples.View' : 'Samples.Edit';
  }
  if (path.startsWith('/api/experiment/')) {
    if (path.includes('hdfx')) return 'Hdfx.View';
    if (path.includes('/fail') || path.includes('conditionallyapprove')) return 'Experiments.Approve';
    if (path === Endpoints.experiments.add.toLowerCase()) return 'Experiments.Create';
    if ([Endpoints.experiments.bySampleId, Endpoints.experiments.byStatuses, Endpoints.experiments.getAll, Endpoints.experiments.searchPendingFailed].some((endpoint) => path === endpoint.toLowerCase())) return 'Experiments.View';
    return method === 'GET' ? 'Experiments.View' : 'Experiments.Edit';
  }
  if (path.startsWith('/api/attachment/')) {
    if (method === 'GET') return 'Attachments.View';
    return method === 'DELETE' ? 'Attachments.Delete' : 'Attachments.Create';
  }
  if (path.startsWith('/api/coaattachment/')) {
    if (method === 'GET') return 'COA.View';
    return method === 'DELETE' ? 'COA.Delete' : 'COA.Edit';
  }
  if (path.startsWith('/api/qareport/')) {
    if (path.includes('approvetestcertificate')) return 'QAReports.ApproveTestCertificate';
    if (path === Endpoints.qaReports.approve.toLowerCase()) return 'QAReports.Approve';
    if (path === Endpoints.qaReports.save.toLowerCase()) return 'QAReports.Edit';
    return 'QAReports.View';
  }
  if (path.startsWith('/api/packager/') || path === '/api/packager' || path.startsWith('/api/packaging/') || path.startsWith('/api/packagingslip/')) {
    if (path === Endpoints.vendorMgmt.addPackagerVendor.toLowerCase() || path === Endpoints.vendorMgmt.removePackagerVendor.toLowerCase()
      || path.includes('productassignments') || path.includes('addpackager') || path.includes('remove')) return 'Packaging.ManageAssignments';
    if (path.includes('source') || path.includes('search') || method === 'GET') return 'Packaging.View';
    return method === 'POST' ? 'Packaging.Create' : 'Packaging.Edit';
  }
  if (path === '/api/coq' && method === 'DELETE') return 'COQ.Delete';
  if (path.startsWith('/api/coq/')) {
    if (path.includes('delete') || path.includes('ponumbers')) return 'COQ.Delete';
    if (path.includes('generate')) return 'COQ.Generate';
    return 'COQ.View';
  }
  if (path.startsWith('/api/reports/')) {
    const reportId = String(request.body.reportId ?? request.body.reportid ?? request.params.reportId ?? request.params.reportid ?? '').toLowerCase();
    if (path.endsWith('/run') && ['metrics', 'missing-po-summary', 'sap-po-log', 'unlisted-material-exception', 'missing-po-exception', 'unlisted-vendor-exception'].includes(reportId)) return 'Reports.AdminView';
    return 'Reports.View';
  }
  if (path.startsWith('/api/sap/')) return method === 'GET' ? 'Reports.View' : 'Reports.Export';
  if (path.startsWith('/api/hdfx/')) return 'Hdfx.View';
  if (path.startsWith('/api/alert/')) return method === 'GET' ? 'Alerts.View' : 'Alerts.Manage';
  if (path.startsWith('/api/catlevel/') || path.startsWith('/api/categorylevel/')) return method === 'GET' ? 'CategoryLevels.View' : 'CategoryLevels.Manage';
  if (/^\/api\/(?:producttype|qalab|country|state|psl|rawmaterial|securityques)(?:\/|$)/.test(path) && method !== 'GET') return 'ReferenceData.Manage';
  if (path.startsWith('/api/vendor/')) return method === 'GET' ? 'Vendors.View' : 'Vendors.Manage';
  return undefined;
}

function requestPsl(request: MockRequest): string | null {
  const input = Object.fromEntries(Object.entries({ ...request.params, ...request.body }).map(([key, value]) => [key.toLowerCase(), value]));
  const direct = input.pslid ?? input.psl ?? input.pslname;
  if (direct) return String(direct);
  const ids = [...request.path.split('/'), ...Object.values(input).map(String)].filter((value) => /^[\da-f-]{36}$/i.test(value));
  const tables = db();
  const related = (record: MockRecord): MockRecord | undefined => {
    for (const [field, table] of [['vendorId', 'users'], ['productId', 'products'], ['vqcaId', 'vqcas'], ['sampleId', 'samples'], ['coaId', 'coas']] as const) {
      const id = record[field];
      if (id) {
        const parent = tables[table]?.find((entry) => entry.oid === id);
        if (parent) return parent;
      }
    }
    return undefined;
  };
  for (const id of ids) {
    for (const records of Object.values(tables)) {
      const record = records.find((entry) => entry.oid === id);
      if (!record) continue;
      const parent = related(record);
      const psl = record.pslId ?? record.pslID ?? record.pslid ?? record.psl ?? record.pslName
        ?? parent?.pslId ?? parent?.pslID ?? parent?.psl;
      if (psl) return String(psl);
    }
  }
  for (const id of ids) {
    const product = tables.products?.find((entry) => entry.oid === id);
    if (product?.pslId ?? product?.pslID ?? product?.psl) return String(product.pslId ?? product.pslID ?? product.psl);
  }
  return null;
}

export function requireVendorAccess(record: MockRecord): void {
  const user = currentUser();
  if (user.portal === 'QCDOC-VENDOR' && record.vendorId !== (user.vendorId ?? user.userId)) throw new Error('This record belongs to another supplier.');
}

export function publicIdentity(record: MockRecord): MockRecord {
  return Object.fromEntries(Object.entries(record).filter(([key]) => !/password|securityanswer|token|secret/i.test(key))) as MockRecord;
}

export function registerIdentity(): void {
  route('POST', Endpoints.auth.login, (request) => {
    const input = values(request);
    const user = db().users.find((record) => record.portal === input.portal && String(record.userName).toLowerCase() === String(input.username).trim().toLowerCase());
    const expected = user?.password ?? (user?.userId === ADMIN_ID ? 'Noman123@' : 'Demo123@');
    if (!user || input.password !== expected || user.portal !== input.portal) throw new Error('Invalid user name or password.');
    if (!user.isApproved || user.isLockedOut) throw new Error('This test account is inactive or locked.');
    assertDirectoryIdentity(user);
    const identity = { ...publicIdentity(user), accessProfile: user.portal === 'QCDOC' ? accessProfileFor(user) : undefined };
    return { accessToken: `mock-session:${user.userId}`, tokenType: 'Bearer', expiresIn: 28800,
      expiresAt: new Date(Date.now() + 28800000).toISOString(), user: identity };
  });
  route('GET', Endpoints.auth.me, () => {
    const user = currentUser();
    return { ...publicIdentity(user), accessProfile: user.portal === 'QCDOC' ? accessProfileFor(user) : undefined };
  });
  route('POST', Endpoints.auth.forgotPassword, (request) => {
    const input = values(request);
    const user = db().users.find((record) => String(record.userName).toLowerCase() === String(input.username).trim().toLowerCase() && record.portal === input.portal);
    if (!user || !user.isApproved || user.isLockedOut) throw new Error('No active test account matches this portal and user name.');
    return { userName: user.userName, securityQuestion: user.securityQuestion ?? 'What is your test project?' };
  });
  route('POST', Endpoints.auth.resetPassword, (request) => {
    const input = values(request);
    const user = db().users.find((record) => String(record.userName).toLowerCase() === String(input.username).trim().toLowerCase() && record.portal === input.portal);
    if (!user || !user.isApproved || user.isLockedOut || String(input.securityanswer ?? '').trim().toLowerCase() !== String(user.securityAnswer ?? user.passwordAnswer ?? 'qcdoc').trim().toLowerCase()) throw new Error('The test account or security answer is incorrect.');
    if (typeof input.newpassword !== 'string' || input.newpassword.length < 8) throw new Error('Password must contain at least 8 characters.');
    update('users', user.oid, { password: input.newpassword });
    return true;
  });
  route('POST', Endpoints.auth.logout, () => true);
  route('POST', Endpoints.auth.changePassword, (request) => {
    const user = currentUser();
    const input = values(request);
    if (input.oldpassword !== (user.password ?? (user.userId === ADMIN_ID ? 'Noman123@' : 'Demo123@'))) throw new Error('Current password is incorrect.');
    if (String(input.newpassword).length < 8) throw new Error('Password must contain at least 8 characters.');
    update('users', user.oid, { password: input.newpassword });
    return true;
  });
  route('POST', Endpoints.auth.requestAccess, (request) => {
    return submitInternalRequest(request.body);
  });
  route('POST', Endpoints.auth.registerVendor, (request) => {
    const input = request.body;
    if (!input.email || !input.userName) throw new Error('User name and email are required.');
    assertUnregistered(input);
    assertNoPendingRequest(input);
    const profile = { ...editableProfile(input), userName: String(input.userName).trim(), ...profilePslPatch(input) };
    const credentials = Object.fromEntries(['password', 'securityQuestion', 'securityAnswer'].filter((key) => input[key] !== undefined).map((key) => [key, input[key]]));
    if (useAuthStore.getState().accessToken) {
      const scope = assertAdministrator(currentUser(), 'Vendors.Manage');
      const parent = input.vendorId ? find('users', input.vendorId) : undefined;
      if (parent && (parent.portal !== 'QCDOC-VENDOR' || parent.isUser)) throw new Error('A vendor company account is required.');
      if (input.isUser && !parent) throw new Error('Select a parent vendor company.');
      if (parent) assertRecordScope(scope, parent);
      const effectiveProfile = { ...profile, ...(parent ? profilePslPatch({ psl: parent.psl }) : {}), company: parent?.company ?? input.company };
      assertRecordScope(scope, effectiveProfile);
      const roles = replacementAssignments(scope, { oid: '', portal: 'QCDOC-VENDOR', roles: [] }, input.roles ?? ['Vendor', 'External User']);
      const user = insert('users', { ...effectiveProfile, ...credentials, portal: 'QCDOC-VENDOR', isInternal: false,
        ...assignmentPatch(roles), isApproved: true, isActive: true, isLockedOut: false,
        fullName: `${input.firstName ?? ''} ${input.lastName ?? ''}`.trim(), isVendor: !input.isUser,
        isUser: Boolean(input.isUser),
        packaging: { read: true, write: false, admin: false }, creationDate: new Date().toISOString() });
      update('users', user.oid, { userId: user.oid, providerUserKey: user.oid, vendorId: parent?.oid ?? user.oid });
      if (!input.isUser) {
        const vendor = insert('vendors', { ...user, name: user.company });
        update('vendors', vendor.oid, { oid: user.oid });
      }
      return { success: true, isApproved: true, userId: user.oid, userName: user.userName, message: 'Vendor test account created.' };
    }
    // Public vendor signup remains a request, never an immediate grant of supplied roles.
    const record = insert('userRequests', { ...profile, ...credentials, portal: 'QCDOC-VENDOR', status: 16, statusId: 16,
      reqStatusName: 'Created', statusName: 'Created', createdBy: profile.userName,
      fullName: `${input.firstName ?? ''} ${input.lastName ?? ''}`.trim() });
    return { success: true, isApproved: false, userId: record.oid, userName: input.userName, message: 'Access request saved for local administrator approval.' };
  });
}